import { Controller } from "@hotwired/stimulus"

// this is tightly coupled with views/backoffice/quoted_wares_proposals/_milestones_fields.html.haml
export default class extends Controller {
  static targets = [
    "sliId",
    "title",
    "description",
    "basePrice",
    "quantity",
    "quantityError",
    "retailPrice",
    "wholesalePrice",
    "wholesalePriceDisplay",
    "wholesaleSubtotal",
    "wholesaleSubtotalDisplay",
    "minFeeUsedMessage",
    "fieldsLockedMessage",
    "hiddenCommissionFeeItem",
    "referentialMilestone",
    "passThroughCost"
  ];
  static values = {
    currencySelector: String,
    savedLineItemAccess: { type: Boolean, default: false }
  }
  static outlets = ["transaction-fee"];

  connect() {
    this.handleFeeChange();
    this.recalculate();
    this.validateQuantity();
  }

  onCurrencyChange(event) {
    if (!event.target.matches(this.currencySelectorValue)) return;
    this.recalculate();
  }

  get isPassThroughCostEnabled() {
    return this.hasPassThroughCostTarget && this.passThroughCostTarget.value === "true";
  }

  handleFeeChange() {
    const currentFee = this.transactionFeeOutlet.getFee()
    const enableFields = currentFee.feeSelected
    this.toggleField(this.basePriceTarget, enableFields)
    this.toggleField(this.quantityTarget, enableFields)
    if (!enableFields) {
      this.fieldsLockedMessageTarget.classList.remove('d-none')
    } else {
      this.fieldsLockedMessageTarget.classList.add('d-none')
    }
    if (this.savedLineItemAccessValue) {
      if (this.hasTitleTarget) { this.toggleField(this.titleTarget, enableFields) }
      this.toggleField(this.descriptionTarget, enableFields)
    }
  }

  validateQuantity() {
    this.toggleQuantityError(this.quantityBelowMinimum)
  }

  // shown only on change, so a value typed past the minimum doesn't flash an error mid-entry
  clearQuantityError() {
    if (!this.quantityBelowMinimum) this.toggleQuantityError(false)
  }

  get quantityBelowMinimum() {
    return parseFloat(this.quantityTarget.value) < parseFloat(this.quantityTarget.min)
  }

  toggleQuantityError(show) {
    if (!this.hasQuantityErrorTarget) return
    this.quantityTarget.classList.toggle("is-invalid", show)
    this.quantityTarget.setAttribute("aria-invalid", show)
  }

  toggleField(element, toggle) {
    element.disabled = !toggle;
    element.title = toggle ? "Please specify your fee assignment preference before entering any milestone details." : "";
  }

  get currencyPrecision() {
    const currencySelect = document.querySelector(this.currencySelectorValue)
    const precision = parseInt(currencySelect?.selectedOptions?.[0]?.dataset?.exponent, 10)
    if (Number.isFinite(precision)) {
      return precision
    } else {
      return 2
    }
  }

  formatMoney(amount) {
    const precision = this.currencyPrecision
		return amount.toFixed(precision)
  }

  get currencyCode() {
    return document.querySelector(this.currencySelectorValue)?.value || 'USD'
  }

  formatMoneyDisplay(amount) {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currencyCode }).format(amount)
  }

  refreshDisplayValues() {
    if (this.hasWholesalePriceDisplayTarget) {
      this.wholesalePriceDisplayTarget.value = this.formatMoneyDisplay(parseFloat(this.wholesalePriceTarget.value) || 0)
    }
    if (this.hasWholesaleSubtotalDisplayTarget) {
      this.wholesaleSubtotalDisplayTarget.value = this.formatMoneyDisplay(parseFloat(this.wholesaleSubtotalTarget.value) || 0)
    }
  }

  /* eslint-disable max-statements */
  recalculate() {
    const currentFee = this.transactionFeeOutlet.getFee()
    const basePrice = parseFloat(this.basePriceTarget.value.replace(/,/g, ''))
    if (!Number.isFinite(basePrice)) { return }

    // Fee cap milestones are priced server-side by AdjustFeeCap — skip recalculation entirely.
    if (this.hiddenCommissionFeeItemTarget.value === "true") { return }

    const is_referential_item = this.referentialMilestoneTarget.value !== ""

    const quantity = parseFloat(this.quantityTarget.value)
    let retailPrice = basePrice
    let wholesalePrice = basePrice
    let min_fee_used = false
    const customerFee = this.isPassThroughCostEnabled ? 0 : currentFee.customerFee
    const providerFee = this.isPassThroughCostEnabled ? 0 : currentFee.providerFee

    if (is_referential_item) {
      retailPrice = this.retailPriceTarget.value // don't modify prices from milestones copied from the original PO
    } else if (customerFee > 0) {
      const retailMultiplier = 1 + (customerFee / 100.0)
      const newRetailPrice = this.formatMoney(basePrice * retailMultiplier)
      if (newRetailPrice > 0 && newRetailPrice <= retailPrice) {
        retailPrice = basePrice + 0.01
        min_fee_used = true
      } else {
        retailPrice = newRetailPrice
      }
    }

    if (is_referential_item) {
      wholesalePrice = this.wholesalePriceTarget.value // don't modify prices from milestones copied from the original PO
    } else if (providerFee > 0) {
      const wholesaleMultiplier = (100 - providerFee) / 100
      const newWholesalePrice = this.formatMoney(basePrice * wholesaleMultiplier)
      if (newWholesalePrice > 0 && newWholesalePrice >= wholesalePrice) {
        wholesalePrice = basePrice - 0.01
        min_fee_used = true
      } else {
        wholesalePrice = newWholesalePrice
      }
    }

    this.retailPriceTarget.value = retailPrice
    this.wholesalePriceTarget.value = wholesalePrice
    this.wholesaleSubtotalTarget.value = this.formatMoney(wholesalePrice * quantity)
    this.minFeeUsedMessageTarget.classList.toggle(this.hiddenClass, !min_fee_used)
    this.refreshDisplayValues()

    // Notify the container to update the net subtotal
    this.dispatch("proposal-line-price-changed", { prefix: false });
  }
  /* eslint-enable max-statements */
}
